import { useEffect, useMemo } from 'react';
import {
    type FieldValues,
    type UseFormReturn,
    useWatch,
} from 'react-hook-form';
import {
    type TreeSelectCheckboxSelectionBehavior,
    type TreeSelectProps,
} from '../models/tree-select.types';
import {
    buildNodeMaps,
    hasSameCheckedKeys,
} from '../utils/tree-select-checkbox-selection.utils';
import {
    getCanonicalTreeSelectValue,
    isAncestorsOnlyCheckboxMode,
} from '../utils/tree-select-selection-model.utils';

interface UseTreeSelectCanonicalFieldValueInput {
    checkboxSelectionBehavior?: TreeSelectCheckboxSelectionBehavior;
    formContext: UseFormReturn<FieldValues>;
    name: string;
    options: TreeSelectProps['options'];
    selectionMode?: TreeSelectProps['selectionMode'];
}

export function useTreeSelectCanonicalFieldValue({
    checkboxSelectionBehavior,
    formContext,
    name,
    options,
    selectionMode,
}: UseTreeSelectCanonicalFieldValueInput): void {
    const watchedValue = useWatch({
        control: formContext.control,
        name,
    });
    const shouldSyncCanonicalValue = isAncestorsOnlyCheckboxMode(
        checkboxSelectionBehavior,
        selectionMode,
    );

    const nodeMaps = useMemo(
        () => (shouldSyncCanonicalValue ? buildNodeMaps(options) : undefined),
        [options, shouldSyncCanonicalValue],
    );

    const canonicalValue = useMemo(() => {
        return getCanonicalTreeSelectValue(
            watchedValue,
            nodeMaps,
            shouldSyncCanonicalValue,
        );
    }, [nodeMaps, shouldSyncCanonicalValue, watchedValue]);

    useEffect(() => {
        if (
            !shouldSyncCanonicalValue ||
            !nodeMaps ||
            hasSameCheckedKeys(watchedValue, canonicalValue)
        ) {
            return;
        }

        formContext.setValue(name as never, canonicalValue as never, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
        });
    }, [
        canonicalValue,
        formContext,
        name,
        nodeMaps,
        shouldSyncCanonicalValue,
        watchedValue,
    ]);
}
